import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { QRCode } from 'react-qrcode-logo';
import * as XLSX from 'xlsx';

import { useAppDispatch } from '../../../redux/hooks';
import { setPageTitle } from '../../../redux/slices/PageTitleSlice';
import '../scss/qr-code-input-field.scss';
import MyButton from '../../../shared-components/button/Button';
import CustomFileInput from '../../../shared-components/custom-file-input/CustomFileInput';
import CustomInput from '../../../shared-components/custom-input/CustomInput';
import CustomRadioButton from '../../../shared-components/custom-radio-button/CustomRadioButton';
import CustomToggleButton from '../../../shared-components/custom-toggle-button/CustomToggleButton';
// import PageTitle from '../../../shared-components/page-title/PageTitle';
import SelectField from '../../../shared-components/select-field/SelectField';

import QrConfiguration from './QrConfiguration';
import QrStyle from './QrStyle';
// import QrTemplate from './QrTemplate';

interface ValuesType {
  id: number;
  display: boolean;
  editingText: boolean;
  fontFamily: string;
  fontSize: string;
  key: string;
  text?: string;
}

interface QRValueTypes {
  qrstyle: 'squares' | 'dots' | 'fluid';
  outerEye: number;
  innerEye: number;
  fgColor: string;
  bgColor: string;
  outerColor: string;
  innerColor: string;
  font: string;
  fontWeight: string;
  fontColor: string;
  logoWidth: number;
  logoHeight: string;
  logoTrans: number;
  logo: File[] | null;
  logoName: string;
  qrSize: number;
  qrMargin: number;
  qrMethod: string;
  deviceSNo: string;
  xlFile: File[] | null;
  deviceSerialNumbers: string[] | null | undefined;
}

/**
 * QR Configuration Component
 * @returns
 * QR Configuration
 */
function QrCodeInputField() {
  const { t } = useTranslation('qrCode');
  const dispatch = useAppDispatch();
  const [menu, setMenu] = useState<number>(0);
  const [serialNumbersToDownload, setSerialNumbersToDownload] = useState<string[]>([]);
  const [qrExport, setQrExport] = useState<string>('png');
  const qrReference = useRef<(HTMLElement | null)[]>([]);
  const [qrValues, setQrValues] = useState<QRValueTypes>({
    qrstyle: 'squares',
    outerEye: 0,
    innerEye: 0,
    fgColor: '#000000',
    bgColor: '#ffffff',
    outerColor: '#000000',
    innerColor: '#000000',
    font: '',
    fontWeight: '',
    fontColor: '',
    logoWidth: 50,
    logoHeight: 'auto',
    logoTrans: 100,
    logo: null,
    logoName: '',
    qrSize: 150,
    qrMargin: 10,
    qrMethod: '',
    deviceSNo: '',
    xlFile: null,
    deviceSerialNumbers: null,
  });

  const [values, setValues] = useState<ValuesType[]>([
    {
      id: 1,
      key: 'qr-code',
      text: t('qrCode'),
      display: true,
      fontFamily: 'Inter',
      fontSize: '16',
      editingText: false,
    },
    {
      id: 2,
      key: 'SN',
      text: 'SN001',
      display: true,
      fontFamily: 'Inter',
      fontSize: '16',
      editingText: false,
    },
    {
      id: 3,
      key: 'T1',
      text: t('sampleText'),
      display: true,
      fontFamily: 'Inter',
      fontSize: '16',
      editingText: false,
    },
    {
      id: 4,
      key: 'T2',
      text: t('brand'),
      display: true,
      fontFamily: 'Inter',
      fontSize: '16',
      editingText: false,
    },
  ]);

  /* eslint-disable unicorn/consistent-function-scoping */
  /**
   * Function to handle input changes in row configuration
   * @param name
   * Getting a name of the input from Drag and Drop Component
   * @param value
   * Getting value of the input from drag and drop component
   */
  const handleChange = (name: string, value: string | number | boolean) => {
    const splittedName = name.split('/'); // Name passed as key/ObjectKey
    const [key, keyName] = splittedName;
    setValues((previous: any[]) =>
      previous.map((eachValue: any) => {
        let updatedValue = eachValue;
        if (updatedValue.key === key) {
          updatedValue = {
            ...updatedValue,
            [keyName]: value,
          };
        }
        return updatedValue;
      }),
    );
  };

  const styleComponents = [
    {
      index: 0,
      component: (
        <QrConfiguration
          handleChange={handleChange}
          values={values}
          setValues={setValues}
          serialNumbersToDownload={serialNumbersToDownload}
        />
      ),
    },
    {
      index: 1,
      component: <QrStyle qrValues={qrValues} setQrValues={setQrValues} />,
    },
    {
      index: 2,
      // component: <QrTemplate />,
    },
  ];

  /**
   * Handling Selected Components
   * @param index
   * Updating the index number toggle button to state
   */
  const handelSelection = (index: number) => {
    setMenu(index);
  };

  /**
   * Handling Export Type
   * @param value
   * Getting a QR code export types value
   */
  const handleQRExport = (value: any) => {
    setQrExport(value);
  };

  /**
   * Handing QR Code Download
   */
  const handleQRDownload = async () => {
    if (qrExport === 'png' && serialNumbersToDownload.length > 0) {
      // Download multiple PNGs
      for (let initial = 0; initial < qrReference.current.length; initial += 1) {
        const element = qrReference.current[initial];
        if (element instanceof HTMLElement) {
          // eslint-disable-next-line no-await-in-loop
          const canvas = await html2canvas(element);
          const data = canvas.toDataURL('image/png');
          const link = document.createElement('a');

          link.href = data;
          // Use the serial number or index in the filename
          const serialNumber = serialNumbersToDownload[initial] || initial;
          link.download = `QRCode_${serialNumber}.png`;

          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          // Add a small delay between downloads to avoid browser restrictions
          // eslint-disable-next-line no-await-in-loop
          await new Promise((resolve) => {
            setTimeout(resolve, 100);
          });
        }
      }
    } else if (qrExport === 'pdf' && serialNumbersToDownload.length > 0) {
      /* eslint-disable-next-line new-cap */
      const pdf = new jsPDF();
      let isFirstPage = true;

      for (let initial = 0; initial < qrReference.current.length; initial += 1) {
        const element = qrReference.current[initial];
        if (element instanceof HTMLElement) {
          // eslint-disable-next-line no-await-in-loop
          const canvas = await html2canvas(element);
          const imgData = canvas.toDataURL('image/png');

          if (!isFirstPage) pdf.addPage();
          pdf.addImage(imgData, 'PNG', 55, 10, 0, 0);
          isFirstPage = false;
        }
      }
      const count = qrValues?.deviceSerialNumbers?.length || '';
      pdf.save(`QRCode${count}.pdf`);
    } else {
      // Default single PNG download (fallback)
      const element = qrReference.current?.[0];
      if (!element) {
        return;
      }
      const canvas = await html2canvas(element);
      const data = canvas.toDataURL('image/png');
      const link = document.createElement('a');

      link.href = data;
      const count = qrValues?.deviceSerialNumbers?.length || '';
      link.download = `QRCode${count}.png`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  /**
   * Handling QR Method
   */
  const handleChangeFunction = (name: string, value: any) => {
    setQrValues((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  /**
   * Handling file remove
   * @param name
   * Getting input name
   */
  const handleRemoveFile = (
    name: string,
    // url: string,
    // indexToRemove: number,
    // type: 'newFile' | 'existingFile',
  ) => {
    if (name) {
      setQrValues({
        ...qrValues,
        [name]: null,
      });
    }
    if (name === 'xlFile') {
      setSerialNumbersToDownload([]);
    }
  };

  useEffect(() => {
    if (qrValues.xlFile && qrValues.xlFile.length > 0) {
      qrValues.xlFile.forEach((eachFile) => {
        // Check if the file is a valid Excel or CSV file
        if (
          eachFile.type.includes('excel') ||
          eachFile.name.endsWith('.xlsx') ||
          eachFile.name.endsWith('.xls') ||
          eachFile.name.endsWith('.csv') ||
          eachFile.type.includes('csv')
        ) {
          const reader = new FileReader();

          /**
           * Handling CSV file data
           * @param event
           */
          reader.onload = (event) => {
            if (event.target?.result) {
              const result = event?.target?.result;

              // Handle CSV files
              if (eachFile.name.endsWith('.csv') || eachFile.type.includes('csv')) {
                const csvData = result as string;
                const lines = csvData.split('\n');
                const deviceSNList: string[] = [];

                // Assuming first line is header
                const headers = lines[0].split(',').map((h) => h.trim());
                const deviceSNIndex = headers.findIndex((h) => h === 'SerialNumbers');

                if (deviceSNIndex !== -1) {
                  for (let index = 1; index < lines.length; index += 1) {
                    if (lines[index].trim()) {
                      const MultiQRvalues = lines[index].split(',');
                      if (MultiQRvalues[deviceSNIndex]) {
                        deviceSNList.push(MultiQRvalues[deviceSNIndex].trim());
                      }
                    }
                  }
                }

                setQrValues((previous) => ({
                  ...previous,
                  deviceSerialNumbers: deviceSNList,
                }));
                setSerialNumbersToDownload(deviceSNList);
              }
              // Handle Excel files (existing code)
              else {
                let data: Uint8Array;

                if (result instanceof ArrayBuffer) {
                  data = new Uint8Array(result);
                } else {
                  const stringResult = result as string;
                  const buffer = new ArrayBuffer(stringResult.length);
                  const view = new Uint8Array(buffer);
                  for (let index = 0; index < stringResult.length; index += 1) {
                    view[index] = stringResult.charCodeAt(index);
                  }
                  data = new Uint8Array(buffer);
                }

                const workbook = XLSX.read(data, { type: 'array' });
                const sheetName = workbook.SheetNames[0];
                const sheetData = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName]);

                const deviceSnList: string[] = [];
                sheetData.forEach((eachRow: any) => {
                  if (eachRow.DeviceSn) {
                    deviceSnList.push(eachRow.DeviceSn.toString());
                  }
                });

                setQrValues((previous) => ({
                  ...previous,
                  deviceSerialNumbers: deviceSnList,
                }));
                setSerialNumbersToDownload(deviceSnList);
              }
            }
          };

          // Use appropriate read method based on file type
          if (eachFile.name.endsWith('.csv') || eachFile.type.includes('csv')) {
            reader.readAsText(eachFile); // Read CSV as text
          } else {
            reader.readAsArrayBuffer(eachFile); // Read Excel as array buffer
          }
        }
      });
    }
  }, [qrValues.xlFile]);

  useEffect(() => {
    if (qrValues.deviceSNo) {
      setSerialNumbersToDownload([qrValues.deviceSNo]);
    }
  }, [qrValues.deviceSNo]);

  useEffect(() => {
    dispatch(setPageTitle(t('qrCodeGenerator')));
  }, []);

  return (
    <div className="qr-code-field-wrap">
      <div className="qr-code-field-top-row">
        <div className="qr-code-logo-input">
          {/* <PageTitle title={t('qrCodeGenerator')} /> */}
          <div className="input-wrap">
            <SelectField
              options={[
                { label: t('singleQR'), value: 'singleQR' },
                { label: t('multiQR'), value: 'MultiQR' },
              ]}
              placeholder={t('qrType')}
              selectedValue={qrValues.qrMethod}
              name="qrMethod"
              onChangeValue={handleChangeFunction}
            />
            {qrValues.qrMethod === 'MultiQR' ? (
              <CustomFileInput
                acceptedFileType=".xls,.xlsx,.csv"
                placeholder={qrValues.xlFile ? `${qrValues.xlFile}` : t('chooseFile')}
                name="xlFile"
                selectedFiles={qrValues.xlFile}
                onChangeValue={handleChangeFunction}
                unselectHandler={handleRemoveFile}
                valueRequired
              />
            ) : (
              <CustomInput
                type="text"
                name="deviceSNo"
                placeholder={t('enterDeviceSNo')}
                value={qrValues.deviceSNo}
                onChangeValue={handleChangeFunction}
              />
            )}

            <CustomFileInput
              acceptedFileType="jpeg"
              placeholder={qrValues.logo ? `${qrValues.logo}` : t('uploadLogo')}
              name="logo"
              selectedFiles={qrValues.logo}
              onChangeValue={handleChangeFunction}
              unselectHandler={handleRemoveFile}
              valueRequired
            />
            <SelectField
              options={[{ label: t('templates'), value: 'templates' }]}
              placeholder={t('selectTemplate')}
              selectedValue="templates"
              name="qrTemplates"
              onChangeValue={handleChangeFunction}
            />
          </div>
        </div>
      </div>
      <div className="qr-code-mid-row">
        <div className="qr-code-editor">
          <CustomToggleButton
            labels={[t('qrConfiguration'), t('qrStyle'), t('template')]}
            defaultActiveIndex={0}
            onToggle={handelSelection}
          />
          {styleComponents.map((component) => (
            <div>{menu === component.index ? component.component : null}</div>
          ))}
        </div>
        <div className="qr-code-wrap">
          <div className="qr-code-preview">
            <div className="qr-code-container">
              {values.map((value) => (
                <div>
                  {value.key === 'qr-code' ? (
                    <div>
                      <QRCode
                        qrStyle={qrValues.qrstyle}
                        eyeRadius={[
                          {
                            outer: qrValues.outerEye,
                            inner: qrValues.innerEye,
                          },
                          {
                            outer: qrValues.outerEye,
                            inner: qrValues.innerEye,
                          },
                          {
                            outer: qrValues.outerEye,
                            inner: qrValues.innerEye,
                          },
                        ]}
                        fgColor={qrValues.fgColor}
                        bgColor={qrValues.bgColor}
                        eyeColor={[
                          {
                            outer: qrValues.outerColor,
                            inner: qrValues.innerColor,
                          },
                          {
                            outer: qrValues.outerColor,
                            inner: qrValues.innerColor,
                          },
                          {
                            outer: qrValues.outerColor,
                            inner: qrValues.innerColor,
                          },
                        ]}
                        logoImage={
                          qrValues.logo ? URL.createObjectURL(qrValues.logo[0]) : undefined
                        }
                        logoWidth={qrValues.logoWidth}
                        logoOpacity={qrValues.logoTrans}
                        size={qrValues.qrSize}
                        quietZone={qrValues.qrMargin}
                      />
                    </div>
                  ) : (
                    <p
                      style={{
                        fontFamily: qrValues.font || value.fontFamily,
                        color: qrValues.fontColor,
                        fontWeight: qrValues.fontWeight,
                        fontSize: `${value.fontSize}px`,
                        display: `${value.display === true ? '' : 'none'}`,
                      }}>
                      {value.key === 'SN' ? serialNumbersToDownload[0] || value.text : value.text}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
          {/* Hide this container by give max-height and overflow hidden for parent container */}
          <div className="qr-code-wrap" style={{ opacity: '0', pointerEvents: 'none' }}>
            {/* Don't edit this inline CSS */}
            {serialNumbersToDownload.map((serial, index) => (
              <div
                className="qr-code-preview"
                ref={(element) => {
                  qrReference.current[index] = element;
                }}>
                <div className="qr-code-container">
                  {values.map((value) => (
                    <div>
                      {value.key === 'qr-code' ? (
                        <div>
                          <QRCode
                            qrStyle={qrValues.qrstyle}
                            eyeRadius={[
                              {
                                outer: qrValues.outerEye,
                                inner: qrValues.innerEye,
                              },
                              {
                                outer: qrValues.outerEye,
                                inner: qrValues.innerEye,
                              },
                              {
                                outer: qrValues.outerEye,
                                inner: qrValues.innerEye,
                              },
                            ]}
                            fgColor={qrValues.fgColor}
                            bgColor={qrValues.bgColor}
                            eyeColor={[
                              {
                                outer: qrValues.outerColor,
                                inner: qrValues.innerColor,
                              },
                              {
                                outer: qrValues.outerColor,
                                inner: qrValues.innerColor,
                              },
                              {
                                outer: qrValues.outerColor,
                                inner: qrValues.innerColor,
                              },
                            ]}
                            logoImage={
                              qrValues.logo ? URL.createObjectURL(qrValues.logo[0]) : undefined
                            }
                            logoWidth={qrValues.logoWidth}
                            logoOpacity={qrValues.logoTrans}
                            size={qrValues.qrSize}
                            quietZone={qrValues.qrMargin}
                          />
                        </div>
                      ) : (
                        <p
                          style={{
                            fontFamily: qrValues.font || value.fontFamily,
                            color: qrValues.fontColor,
                            fontWeight: qrValues.fontWeight,
                            fontSize: `${value.fontSize}px`,
                            display: `${value.display === true ? '' : 'none'}`,
                          }}>
                          {value.key === 'SN' ? serial || value.text : value.text}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="qr-export-container">
            <div className="qr-export-type">
              <CustomRadioButton
                selected={qrExport === 'png'}
                value="png"
                label={t('png')}
                name="png"
                onChangeFunc={handleQRExport}
              />
              <CustomRadioButton
                selected={qrExport === 'pdf'}
                value="pdf"
                label={t('pdf')}
                name="pdf"
                onChangeFunc={handleQRExport}
              />
            </div>
            <div className="qr-download">
              <div className="save">
                <MyButton
                  label={t('saveTemplate')}
                  buttonType="submit"
                  onClickFunc={() => handleChange}
                />
              </div>
              <div className="download">
                <MyButton
                  label={t('download')}
                  buttonType="submit"
                  onClickFunc={handleQRDownload}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default QrCodeInputField;
